import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { brand } from "../../config/brand";
import { SERVICE_NAV } from "../../data/static";
import { getDestinations } from "../../lib/api";
import { useConsultation } from "../Consultation";
import Icon from "../Icon";

const FALLBACK_DESTS = [
  { slug: "usa", name: "USA" }, { slug: "uk", name: "UK" }, { slug: "canada", name: "Canada" },
  { slug: "australia", name: "Australia" }, { slug: "new-zealand", name: "New Zealand" },
  { slug: "germany", name: "Germany" }, { slug: "singapore", name: "Singapore" },
  { slug: "malaysia", name: "Malaysia" }, { slug: "mauritius", name: "Mauritius" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [destinations, setDestinations] = useState(FALLBACK_DESTS);
  const location = useLocation();
  const openConsultation = useConsultation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    getDestinations()
      .then((d) => setDestinations(d.map((x) => ({ slug: x.slug, name: x.name }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const solid = scrolled || mobileOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        solid ? "border-b border-slate-200/80 bg-white/90 shadow-[0_4px_24px_rgba(27,47,91,0.06)] backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-4 px-4 sm:px-6 lg:h-20 lg:px-10">
        <Link to="/" className="flex shrink-0 items-center" aria-label="Suvera Academy home" data-testid="header-logo-link">
          <img
            src={solid ? brand.logo : brand.logoWhite}
            alt="Suvera Academy"
            className="h-7 w-auto transition-opacity duration-300 lg:h-8"
          />
        </Link>

        <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary" data-testid="desktop-nav">
          <NavItem to="/" label="Home" solid={solid} end />
          <NavItem to="/about" label="About Us" solid={solid} />
          <Dropdown
            label="Study Destinations"
            to="/destinations"
            solid={solid}
            items={destinations.map((d) => ({ label: d.name, to: `/destinations/${d.slug}`, testid: `nav-destination-${d.slug}` }))}
            testid="nav-destinations-dropdown"
          />
          <NavItem to="/institutes" label="Institutes" solid={solid} />
          <NavItem to="/scholarships" label="Scholarships" solid={solid} />
          <NavItem to="/pathway-programs" label="Pathway Programs" solid={solid} />
          <Dropdown
            label="Services"
            to="/services"
            solid={solid}
            items={SERVICE_NAV.map((s) => ({ label: s.label, to: `/services#${s.anchor}`, testid: `nav-service-${s.anchor}` }))}
            testid="nav-services-dropdown"
          />
          <NavItem to="/partner-with-us" label="Partner With Us" solid={solid} />
          <NavItem to="/news" label="News" solid={solid} />
          <NavItem to="/contact" label="Contact" solid={solid} />
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={openConsultation}
            className={`hidden rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 md:inline-flex ${
              solid ? "bg-navy text-white hover:bg-navy-light" : "bg-white text-navy hover:bg-dot-amber hover:text-navy-dark"
            }`}
            data-testid="header-book-consultation-button"
          >
            Book Free Consultation
          </button>
          <button
            className={`inline-flex rounded-lg p-2 transition-colors duration-200 xl:hidden ${solid ? "text-navy hover:bg-sand" : "text-white hover:bg-white/10"}`}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            <Icon name={mobileOpen ? "X" : "Menu"} className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <MobileMenu destinations={destinations} onConsultation={openConsultation} />
        )}
      </AnimatePresence>
    </header>
  );
}

function NavItem({ to, label, solid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `rounded-full px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${
          isActive
            ? solid ? "bg-navy/5 text-navy" : "bg-white/10 text-white"
            : solid ? "text-slate-600 hover:bg-sand hover:text-navy" : "text-white/80 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function Dropdown({ label, to, items, solid, testid }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timeout = useRef(null);
  const enter = () => { clearTimeout(timeout.current); setOpen(true); };
  const leave = () => { timeout.current = setTimeout(() => setOpen(false), 120); };

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button
        onClick={() => navigate(to)}
        onFocus={enter}
        className={`flex items-center gap-1 rounded-full px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${
          solid ? "text-slate-600 hover:bg-sand hover:text-navy" : "text-white/80 hover:bg-white/10 hover:text-white"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={testid}
      >
        {label}
        <Icon name="ChevronDown" className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-full w-60 pt-2"
          >
            <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-[0_16px_48px_rgba(13,24,51,0.14)]">
              <Link
                to={to}
                className="block rounded-lg px-3 py-2 text-[13px] font-semibold text-navy transition-colors duration-150 hover:bg-sand"
                data-testid={`${testid}-all`}
              >
                View All
              </Link>
              <div className="my-1 h-px bg-slate-100" />
              {items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-3 py-2 text-[13px] text-slate-600 transition-colors duration-150 hover:bg-sand hover:text-navy"
                  data-testid={item.testid}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileMenu({ destinations, onConsultation }) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-slate-100 bg-white xl:hidden"
      data-testid="mobile-menu"
    >
      <nav className="space-y-1 px-4 py-6 sm:px-6" aria-label="Mobile">
        {[
          { to: "/", label: "Home" }, { to: "/about", label: "About Us" },
          { to: "/institutes", label: "Institutes" }, { to: "/scholarships", label: "Scholarships" },
          { to: "/pathway-programs", label: "Pathway Programs" }, { to: "/partner-with-us", label: "Partner With Us" },
          { to: "/news", label: "News" }, { to: "/contact", label: "Contact" },
        ].map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `block rounded-lg px-4 py-3 text-base font-medium transition-colors duration-150 ${isActive ? "bg-navy/5 text-navy" : "text-slate-700 hover:bg-sand"}`
            }
          >
            {l.label}
          </NavLink>
        ))}
        <MobileGroup label="Study Destinations" items={destinations.map((d) => ({ label: d.name, to: `/destinations/${d.slug}` }))} allTo="/destinations" testid="mobile-nav-destinations" />
        <MobileGroup label="Services" items={SERVICE_NAV.map((s) => ({ label: s.label, to: `/services#${s.anchor}` }))} allTo="/services" testid="mobile-nav-services" />
        <button
          onClick={onConsultation}
          className="mt-4 w-full rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white"
          data-testid="mobile-book-consultation-button"
        >
          Book Free Consultation
        </button>
      </nav>
    </motion.div>
  );
}

function MobileGroup({ label, items, allTo, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-base font-medium text-slate-700 hover:bg-sand"
        aria-expanded={open}
        data-testid={testid}
      >
        {label}
        <Icon name="ChevronDown" className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="space-y-0.5 py-1 pl-4">
              <Link to={allTo} className="block rounded-lg px-4 py-2 text-sm font-semibold text-navy hover:bg-sand" data-testid={`${testid}-all`}>
                View All
              </Link>
              {items.map((i) => (
                <Link key={i.to} to={i.to} className="block rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-sand" data-testid={`${testid}-${i.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {i.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
